import { hexToHsl, HSL, hslToString } from './colorUtilities';
import createPalette from './palette';
import { Theme } from './types';

type PropertyValue = string | number | HSL;
export type { Theme };

function valueToString(value: PropertyValue, root: string) {
  switch (root) {
    case 'color':
      if (typeof value === 'string') return value.startsWith('#') ? hslToString(hexToHsl(value)) : value;
      if (Array.isArray(value)) return hslToString(value);
      return undefined;
    case 'radius':
      return `${value}${typeof value === 'number' ? 'px' : ''}`;
  }

  return `${value}`;
}

function flattenThemeToCss(key: string, value: PropertyValue | Record<string, PropertyValue>, root?: string): string {
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.entries(value)
      .map<string>(([propKey, propValue]) => flattenThemeToCss([key, propKey].join('-'), propValue, root || key))
      .join('\n');
  }

  return `--cb-${key}: ${valueToString(value as string, root || key)};`;
}

export function buildTheme({ color = {}, font = {}, radius = {} }: Theme = {}) {
  const colors = { ...color };
  if ('primary' in colors && colors.primary) {
    if (typeof colors.primary === 'string') {
      colors.primary = { 600: colors.primary };
    }

    const palette = createPalette(colors.primary?.['600'] || '#346DEA', 600);
    colors.primary = { ...colors.primary, ...palette };
  }

  if ('secondary' in colors && colors.secondary) {
    if (typeof colors.secondary === 'string') {
      colors.secondary = { 300: colors.secondary };
    }

    const palette = createPalette(colors.secondary?.['300'] || '#D5FF6D', 300);
    colors.secondary = { ...colors.secondary, ...palette };
  }

  const markSymbolColor = `hsl(${valueToString(colors.secondary?.[300]!, 'color')?.replaceAll('%', '%25')})`;
  const markSymbol = `url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="18" height="44" fill="none"%3E%3Cpath fill="${markSymbolColor}" d="M0 19h18v16H0z"/%3E%3C/svg%3E')`;

  return `:root {
  ${flattenThemeToCss('color', colors as PropertyValue)}
  ${flattenThemeToCss('font', font)}
  ${flattenThemeToCss('radius', radius)}
  --cb-mark-symbol: ${markSymbol};
  }`;
}
